import axiosInstance from '../../../shared/api/axiosInstance';
import externalAxiosInstance from '../../../external/services/externalAxiosInstance';
import type { SupportDashboard } from '../../../generated/api/models/supportDashboard';
import type {
  AgentConfig,
  AgentConfigUpdate,
  AvailableUser,
  CsatReport,
  KBArticle,
  KBArticleCreate,
  KBArticleUpdate,
  KBCategory,
  ListArticlesParams,
  ListTicketsParams,
  TicketBulkScope,
  PortalListArticlesParams,
  PortalNotificationPreferences,
  PortalNotificationPreferencesUpdate,
  PortalProductsResponse,
  PortalTicketCreate,
  PortalTicketMuteStatus,
  RoutingRule,
  RoutingRuleCreate,
  RoutingRuleUpdate,
  SLAPolicy,
  SLAPolicyCreate,
  SLAPolicyUpdate,
  Ticket,
  TicketAttachment,
  TicketCreate,
  TicketMessage,
  TicketMessageCreate,
  TicketStats,
  TicketUpdate,
  WidgetConfig,
  WidgetConfigUpdate,
  WidgetEmbedCode,
} from '../types';

const BASE_URL = '/api/v1/support';

// Ticket API
export const ticketApi = {
  list: async (params?: ListTicketsParams): Promise<{ data: Ticket[]; total: number }> => {
    // Convert arrays to comma-separated strings for the backend
    const apiParams = params ? {
      ...params,
      status: params.status?.length ? params.status.join(',') : undefined,
      exclude_status: params.exclude_status?.length ? params.exclude_status.join(',') : undefined,
      priority: params.priority?.length ? params.priority.join(',') : undefined,
    } : undefined;
    const response = await axiosInstance.get(`${BASE_URL}/tickets`, { params: apiParams });
    return response.data;
  },

  get: async (id: string): Promise<Ticket> => {
    const response = await axiosInstance.get(`${BASE_URL}/tickets/${id}`);
    return response.data;
  },

  // `getByNumber` was deleted here: `GET /tickets/by-number/{n}` has never been a
  // route, and nothing called the method — every surface addresses a ticket by id.
  // The number is a display field (`TicketList`, the detail header); if a
  // number-keyed lookup is ever wanted, add the route and use the generated client.

  create: async (data: TicketCreate): Promise<Ticket> => {
    const response = await axiosInstance.post(`${BASE_URL}/tickets`, data);
    return response.data;
  },

  // Portal users create tickets - contact_id derived from auth
  createPortal: async (data: PortalTicketCreate): Promise<Ticket> => {
    const response = await axiosInstance.post('/api/v1/external/support/tickets', data);
    return response.data;
  },

  update: async (id: string, data: TicketUpdate): Promise<Ticket> => {
    const response = await axiosInstance.put(`${BASE_URL}/tickets/${id}`, data);
    return response.data;
  },

  // `assign` was deleted here: `POST /tickets/{id}/assign` has never been a route.
  // Assignment is a field, not an action — both surfaces that perform it write
  // `assigned_agent_id` through the ticket update path (`TicketDetailPage`'s
  // assignee picker via `update`, the list's bulk-assign via `bulkUpdate`), which
  // is what `TicketService.assign_ticket` runs behind. Nothing called this.

  getMessages: async (id: string, includeInternal = true): Promise<TicketMessage[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/tickets/${id}/messages`, {
      params: { include_internal: includeInternal },
    });
    return response.data;
  },

  addMessage: async (id: string, data: TicketMessageCreate): Promise<TicketMessage> => {
    const response = await axiosInstance.post(`${BASE_URL}/tickets/${id}/messages`, data);
    return response.data;
  },

  markRead: async (id: string): Promise<void> => {
    await axiosInstance.post(`${BASE_URL}/tickets/${id}/mark-read`);
  },

  submitCsat: async (id: string, score: number, comment?: string): Promise<Ticket> => {
    const response = await axiosInstance.post(`${BASE_URL}/tickets/${id}/csat`, {
      score,
      comment,
    });
    return response.data;
  },

  getStats: async (agentId?: string, groupId?: string): Promise<TicketStats> => {
    const response = await axiosInstance.get(`${BASE_URL}/tickets/stats`, {
      params: { assigned_agent_id: agentId, assigned_group_id: groupId },
    });
    return response.data;
  },

  // Attachments
  listAttachments: async (ticketId: string): Promise<TicketAttachment[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/tickets/${ticketId}/attachments`);
    return response.data.data;
  },

  uploadAttachment: async (
    ticketId: string,
    file: File,
    messageId?: string
  ): Promise<TicketAttachment> => {
    const formData = new FormData();
    formData.append('file', file);
    if (messageId) {
      formData.append('message_id', messageId);
    }
    const response = await axiosInstance.post(
      `${BASE_URL}/tickets/${ticketId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  },

  getAttachment: async (ticketId: string, attachmentId: string): Promise<TicketAttachment> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/tickets/${ticketId}/attachments/${attachmentId}`
    );
    return response.data.data;
  },

  downloadAttachment: async (ticketId: string, attachmentId: string): Promise<Blob> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/tickets/${ticketId}/attachments/${attachmentId}/download`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  },

  deleteAttachment: async (ticketId: string, attachmentId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/tickets/${ticketId}/attachments/${attachmentId}`);
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/tickets/${id}`);
  },

  // Work item integration
  createWorkItem: async (ticketId: string): Promise<Record<string, unknown>> => {
    const response = await axiosInstance.post(`${BASE_URL}/tickets/${ticketId}/work-item`);
    return response.data;
  },

  // `getLinkedWorkItem` was deleted here: `GET /tickets/{id}/work-item` now declares
  // a response model, so the generated orval client owns it end-to-end (and so do
  // the PUT link / DELETE unlink beside it). `createWorkItem` above stays because
  // the POST returns software_factory's whole work-item dict, which is untyped.

  // Bulk operations
  /**
   * Update tickets, scoped EITHER by an explicit id list OR by the filter set
   * (`ui-list-pages.md` §11 → Select-all-matching).
   *
   * The escalated call sends `filters` — the SAME object the list request was
   * built from — because the browser cannot enumerate rows it never fetched. The
   * server re-derives the tenant, company and sharing bounds itself; these filters
   * can only ever narrow, and it caps the escalation well below the shared ceiling
   * because each ticket update notifies its customer.
   */
  bulkUpdate: async (
    scope: TicketBulkScope,
    data: TicketUpdate
  ): Promise<{ updated: number }> => {
    const body =
      'ids' in scope
        ? { ids: scope.ids, ...data }
        : { match_all_filtered: true, filters: scope.filters, ...data };
    const response = await axiosInstance.post(`${BASE_URL}/tickets/bulk-update`, body);
    return response.data;
  },

  bulkDelete: async (ids: string[]): Promise<{ deleted: number }> => {
    const response = await axiosInstance.post(`${BASE_URL}/tickets/bulk-delete`, { ids });
    return response.data;
  },

  // `getLinkedFiles` was deleted here: it had no callers, and it read
  // `response.data.data` off `GET /tickets/{id}/files`, which now returns the
  // declared `TicketLinkedFileListResponse` ({items, total}) instead of a bare
  // list of `File.to_dict()`. Left in place it would have returned `undefined`
  // the first time anyone wired it up. Use the generated, typed client —
  // `useGetTicketBacklinksApiV1SupportTicketsTicketIdFilesGet`.
};

// Dashboard API
export const dashboardApi = {
  get: async (): Promise<SupportDashboard> => {
    const response = await axiosInstance.get(`${BASE_URL}/dashboard`);
    return response.data;
  },
  getCsatReport: async (startDate: string, endDate: string): Promise<CsatReport> => {
    const response = await axiosInstance.get(`${BASE_URL}/csat-reports`, {
      params: { start_date: startDate, end_date: endDate },
    });
    return response.data;
  },
};

// Knowledge Base API
export const kbApi = {
  // Categories
  listCategories: async (parentId?: string, publicOnly = false): Promise<KBCategory[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/kb/categories`, {
      params: { parent_id: parentId, public_only: publicOnly },
    });
    return response.data;
  },

  getCategory: async (id: string): Promise<KBCategory> => {
    const response = await axiosInstance.get(`${BASE_URL}/kb/categories/${id}`);
    return response.data;
  },

  createCategory: async (data: {
    name: string;
    slug: string;
    description?: string;
    parent_id?: string;
    position?: number;
    icon?: string;
    product_ids?: string[];
    is_public?: boolean;
  }): Promise<KBCategory> => {
    const response = await axiosInstance.post(`${BASE_URL}/kb/categories`, data);
    return response.data;
  },

  updateCategory: async (
    id: string,
    data: Partial<{
      name: string;
      slug: string;
      description: string;
      position: number;
      icon: string;
      product_ids: string[];
      is_public: boolean;
    }>
  ): Promise<KBCategory> => {
    const response = await axiosInstance.patch(`${BASE_URL}/kb/categories/${id}`, data);
    return response.data;
  },

  deleteCategory: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/kb/categories/${id}`);
  },

  // Articles
  listArticles: async (params?: ListArticlesParams): Promise<{ data: KBArticle[]; total: number }> => {
    const response = await axiosInstance.get(`${BASE_URL}/kb/articles`, { params });
    return response.data;
  },

  getArticle: async (id: string): Promise<KBArticle> => {
    const response = await axiosInstance.get(`${BASE_URL}/kb/articles/${id}`);
    return response.data;
  },

  // `getArticleBySlug` was deleted here: `GET /kb/articles/by-slug/{slug}` has never
  // been a route, and nothing called the method. `KBService.get_article_by_slug`
  // exists and is used server-side for slug uniqueness; no surface reads an article
  // by slug, because every article link carries the id.

  createArticle: async (data: KBArticleCreate): Promise<KBArticle> => {
    const response = await axiosInstance.post(`${BASE_URL}/kb/articles`, data);
    return response.data;
  },

  // PUT, not PATCH: the route is `@support_router.put("/kb/articles/{article_id}")`.
  // The sibling `kb/categories/{id}` genuinely IS a patch, which is how the wrong
  // word got typed — and every article Save returned 405 until it was corrected.
  updateArticle: async (id: string, data: KBArticleUpdate): Promise<KBArticle> => {
    const response = await axiosInstance.put(`${BASE_URL}/kb/articles/${id}`, data);
    return response.data;
  },

  publishArticle: async (id: string): Promise<KBArticle> => {
    const response = await axiosInstance.post(`${BASE_URL}/kb/articles/${id}/publish`);
    return response.data;
  },

  archiveArticle: async (id: string): Promise<KBArticle> => {
    const response = await axiosInstance.post(`${BASE_URL}/kb/articles/${id}/archive`);
    return response.data;
  },

  deleteArticle: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/kb/articles/${id}`);
  },

  // `recordView` was deleted here: `POST /kb/articles/{id}/view` has never been a
  // route, and it would have been redundant if it were — `GET /kb/articles/{id}`
  // already calls `KBService.record_view` on every read, which is the one place a
  // view can be counted without trusting the client to report it.

  recordFeedback: async (id: string, helpful: boolean): Promise<void> => {
    await axiosInstance.post(`${BASE_URL}/kb/articles/${id}/feedback`, { helpful });
  },
};

// Portal API (for external users)
const PORTAL_BASE_URL = '/api/v1/external/support';

export const portalApi = {
  listTickets: async (params?: ListTicketsParams): Promise<{ data: Ticket[]; total: number }> => {
    const response = await externalAxiosInstance.get(`${PORTAL_BASE_URL}/tickets`, { params });
    return response.data;
  },

  getTicket: async (id: string): Promise<Ticket> => {
    const response = await externalAxiosInstance.get(`${PORTAL_BASE_URL}/tickets/${id}`);
    return response.data;
  },

  createTicket: async (data: PortalTicketCreate): Promise<Ticket> => {
    const response = await externalAxiosInstance.post(`${PORTAL_BASE_URL}/tickets`, data);
    return response.data;
  },

  getMessages: async (id: string): Promise<TicketMessage[]> => {
    const response = await externalAxiosInstance.get(`${PORTAL_BASE_URL}/tickets/${id}/messages`);
    return response.data;
  },

  addMessage: async (id: string, bodyText: string, bodyHtml?: string): Promise<TicketMessage> => {
    const response = await externalAxiosInstance.post(`${PORTAL_BASE_URL}/tickets/${id}/messages`, {
      body_text: bodyText,
      body_html: bodyHtml,
    });
    return response.data;
  },

  submitCsat: async (id: string, score: number, comment?: string): Promise<Ticket> => {
    const response = await externalAxiosInstance.post(`${PORTAL_BASE_URL}/tickets/${id}/csat`, {
      score,
      comment,
    });
    return response.data;
  },

  resolveTicket: async (id: string): Promise<Ticket> => {
    const response = await externalAxiosInstance.post(`${PORTAL_BASE_URL}/tickets/${id}/resolve`);
    return response.data;
  },

  listAttachments: async (ticketId: string): Promise<TicketAttachment[]> => {
    const response = await externalAxiosInstance.get(`${PORTAL_BASE_URL}/tickets/${ticketId}/attachments`);
    return response.data.data;
  },

  downloadAttachment: async (ticketId: string, attachmentId: string): Promise<Blob> => {
    const response = await externalAxiosInstance.get(
      `${PORTAL_BASE_URL}/tickets/${ticketId}/attachments/${attachmentId}/download`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  listProducts: async (): Promise<PortalProductsResponse> => {
    const response = await externalAxiosInstance.get(`${PORTAL_BASE_URL}/products`);
    return response.data;
  },

  // Knowledge base — the portal twins of `kbApi`'s read + feedback calls. A portal
  // reader carries an external token only, so these must ride externalAxiosInstance
  // against /api/v1/external/support/*; the internal `kbApi` sits behind
  // `require_auth` and 401s for them.
  listKBCategories: async (parentId?: string): Promise<KBCategory[]> => {
    const response = await externalAxiosInstance.get(`${PORTAL_BASE_URL}/kb/categories`, {
      params: { parent_id: parentId },
    });
    return response.data;
  },

  listKBArticles: async (
    params?: PortalListArticlesParams
  ): Promise<{ data: KBArticle[]; total: number }> => {
    const response = await externalAxiosInstance.get(`${PORTAL_BASE_URL}/kb/articles`, { params });
    return response.data;
  },

  getKBArticle: async (id: string): Promise<KBArticle> => {
    const response = await externalAxiosInstance.get(`${PORTAL_BASE_URL}/kb/articles/${id}`);
    return response.data;
  },

  recordKBArticleFeedback: async (id: string, helpful: boolean): Promise<void> => {
    await externalAxiosInstance.post(`${PORTAL_BASE_URL}/kb/articles/${id}/feedback`, { helpful });
  },

  // Notification preferences + per-ticket mute. These routes carry no
  // `response_model`, so orval generates `unknown` for every one of them, and its
  // generated hooks ride the internal client a portal reader has no token for.
  // Hence the hand-written shapes in `../types`.
  getNotificationPreferences: async (): Promise<PortalNotificationPreferences> => {
    const response = await externalAxiosInstance.get(`${PORTAL_BASE_URL}/notifications/preferences`);
    return response.data;
  },

  updateNotificationPreferences: async (
    update: PortalNotificationPreferencesUpdate
  ): Promise<PortalNotificationPreferences> => {
    const response = await externalAxiosInstance.put(
      `${PORTAL_BASE_URL}/notifications/preferences`,
      update
    );
    return response.data;
  },

  getTicketMuteStatus: async (ticketId: string): Promise<PortalTicketMuteStatus> => {
    const response = await externalAxiosInstance.get(
      `${PORTAL_BASE_URL}/notifications/tickets/${ticketId}/mute-status`
    );
    return response.data;
  },

  muteTicket: async (ticketId: string, reason?: string): Promise<PortalTicketMuteStatus> => {
    const response = await externalAxiosInstance.post(
      `${PORTAL_BASE_URL}/notifications/tickets/${ticketId}/mute`,
      { mute_reason: reason ?? null }
    );
    return response.data;
  },

  unmuteTicket: async (ticketId: string): Promise<{ success: boolean; ticket_id: string }> => {
    const response = await externalAxiosInstance.post(
      `${PORTAL_BASE_URL}/notifications/tickets/${ticketId}/unmute`
    );
    return response.data;
  },
};

// SLA Policy API
export const slaApi = {
  list: async (activeOnly = false): Promise<SLAPolicy[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/sla-policies`, {
      params: { active_only: activeOnly },
    });
    return response.data;
  },

  get: async (id: string): Promise<SLAPolicy> => {
    const response = await axiosInstance.get(`${BASE_URL}/sla-policies/${id}`);
    return response.data;
  },

  create: async (data: SLAPolicyCreate): Promise<SLAPolicy> => {
    const response = await axiosInstance.post(`${BASE_URL}/sla-policies`, data);
    return response.data;
  },

  update: async (id: string, data: SLAPolicyUpdate): Promise<SLAPolicy> => {
    const response = await axiosInstance.put(`${BASE_URL}/sla-policies/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/sla-policies/${id}`);
  },
};

// Agent API
export const agentApi = {
  list: async (includeOffline = true): Promise<AgentConfig[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/agents`, {
      params: { include_offline: includeOffline },
    });
    return response.data;
  },

  get: async (userId: string): Promise<AgentConfig> => {
    const response = await axiosInstance.get(`${BASE_URL}/agents/${userId}`);
    return response.data;
  },

  create: async (userId: string, data: AgentConfigUpdate): Promise<AgentConfig> => {
    const response = await axiosInstance.post(`${BASE_URL}/agents/${userId}`, data);
    return response.data;
  },

  update: async (userId: string, data: AgentConfigUpdate): Promise<AgentConfig> => {
    const response = await axiosInstance.put(`${BASE_URL}/agents/${userId}`, data);
    return response.data;
  },

  toggleAvailability: async (userId: string): Promise<AgentConfig> => {
    const response = await axiosInstance.post(`${BASE_URL}/agents/${userId}/toggle-availability`);
    return response.data;
  },

  delete: async (userId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/agents/${userId}`);
  },

  getAvailableUsers: async (): Promise<AvailableUser[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/agents/available-users`);
    return response.data;
  },
};

// Routing Rules API
export const routingRulesApi = {
  list: async (activeOnly = false): Promise<RoutingRule[]> => {
    const response = await axiosInstance.get(`${BASE_URL}/routing-rules`, {
      params: { active_only: activeOnly },
    });
    return response.data;
  },

  get: async (id: string): Promise<RoutingRule> => {
    const response = await axiosInstance.get(`${BASE_URL}/routing-rules/${id}`);
    return response.data;
  },

  create: async (data: RoutingRuleCreate): Promise<RoutingRule> => {
    const response = await axiosInstance.post(`${BASE_URL}/routing-rules`, data);
    return response.data;
  },

  update: async (id: string, data: RoutingRuleUpdate): Promise<RoutingRule> => {
    const response = await axiosInstance.put(`${BASE_URL}/routing-rules/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE_URL}/routing-rules/${id}`);
  },

  reorder: async (ruleIds: string[]): Promise<RoutingRule[]> => {
    const response = await axiosInstance.post(`${BASE_URL}/routing-rules/reorder`, {
      rule_ids: ruleIds,
    });
    return response.data;
  },
};

// Widget API
export const widgetApi = {
  getConfig: async (): Promise<WidgetConfig> => {
    const response = await axiosInstance.get(`${BASE_URL}/widget/config`);
    return response.data;
  },

  updateConfig: async (data: WidgetConfigUpdate): Promise<WidgetConfig> => {
    const response = await axiosInstance.put(`${BASE_URL}/widget/config`, data);
    return response.data;
  },

  regenerateKey: async (): Promise<{ widget_key: string }> => {
    const response = await axiosInstance.post(`${BASE_URL}/widget/regenerate-key`);
    return response.data;
  },

  getEmbedCode: async (): Promise<WidgetEmbedCode> => {
    const response = await axiosInstance.get(`${BASE_URL}/widget/embed-code`);
    return response.data;
  },
};
